import { Input } from '@aigc/antd';
import { AiModal } from '@aigc/components';
import { useOverlayRef } from '@aigc/overlay-hook';
import type { UUIDString } from '@aigc/types';
import { PlusOutlined } from '@ant-design/icons';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQuery } from '@tanstack/react-query';
import { App, Button, Divider } from 'antd';
import { useState } from 'react';
import { useForm, Watch } from 'react-hook-form';
import { array, object, string } from 'yup';
import {
  createTagMutationOptions,
  tagsQueryOptions,
} from '../features/tag/api';
import { TagList } from '../features/tag/components/tag-list';
import type { ScriptTagDto } from '../features/tag/model';
import { FormControl } from './form-control';

const maxTags = 3;

export const TagSelectorModal = (props: {
  initialTagIds?: UUIDString[];
  beforeConfirm?: (tags?: ScriptTagDto[]) => Promise<void>;
}) => {
  const { message } = App.useApp();
  const overlayRef = useOverlayRef();
  const [isPending, setPending] = useState(false);
  const { data: tags, isLoading } = useQuery(tagsQueryOptions);
  const create = useMutation(createTagMutationOptions);

  const form1 = useForm({
    mode: 'onChange',
    defaultValues: {
      ids: props.initialTagIds,
    },
    resolver: yupResolver(
      object({
        ids: array().of(string().required()).max(3),
      }),
    ),
  });

  const form2 = useForm({
    resolver: yupResolver(
      object({
        tag_name: string().nullable().trim().required('请输入标签名称'),
      }),
    ),
  });

  return (
    <AiModal
      title="标签管理"
      loading={isPending}
      onCancel={() => {
        overlayRef.close();
      }}
      onOk={form1.handleSubmit(async (values) => {
        if (isLoading) {
          return;
        }

        if (isPending) {
          return;
        }

        setPending(true);

        try {
          const selectedTags =
            (values.ids?.length
              ? tags?.filter((tag) => values.ids?.includes(tag.id))
              : void 0) ?? [];
          props.beforeConfirm?.(selectedTags);
          overlayRef.close(selectedTags);
        } finally {
          setPending(false);
        }
      })}
    >
      <div>
        <Watch
          control={form1.control}
          name="ids"
          render={(ids) => (
            <p className="mb-3 text-sm text-primary-500">
              已选择 {ids?.length ?? 0}/{maxTags} 个标签
            </p>
          )}
        ></Watch>
        <FormControl
          control={form1.control}
          name="ids"
          render={({ field }) => {
            return (
              <TagList value={field.value} onChange={field.onChange}></TagList>
            );
          }}
        ></FormControl>
      </div>

      <Divider></Divider>

      <FormControl
        control={form2.control}
        name="tag_name"
        layout="vertical"
        label="创建新标签"
        render={({ field }) => (
          <div className="flex items-center gap-2">
            <Input {...field} placeholder="输入新标签名称"></Input>
            <Button
              variant="solid"
              color="primary"
              icon={<PlusOutlined />}
              onClick={form2.handleSubmit(async (values) => {
                await create.mutateAsync(values.tag_name);
                message.success('创建成功');
                form2.reset({ tag_name: '' });
              })}
            >
              创建
            </Button>
          </div>
        )}
      ></FormControl>
    </AiModal>
  );
};
